// © 2026 AttainIP Engineering
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { features } from './config/features';
import { LoadingPlaceholder } from './components/Spinner';
import { RequireAuth } from './components/auth/RequireAuth';
import { RequireOrgAdmin } from './components/auth/RequireOrgAdmin';
import { RequireWorkspace } from './components/auth/RequireWorkspace';
import { EnterpriseAccountGuard } from './components/auth/EnterpriseAccountGuard';
import { CookieConsent } from './components/CookieConsent';
import { ErrorBanner } from './components/ErrorBanner';
import { UpdateAvailableNotice } from './components/UpdateAvailableNotice';
import { SkipToContent } from './components/SkipToContent';
import { useRouteFocus } from './hooks/useRouteFocus';
import { useAuthStore, useTrackingStore } from './store';
import { restoreSessionFromRefreshToken } from './auth/restoreSession';
import { useSessionExpiryWatcher } from './auth/useSessionExpiryWatcher';
import { getStoredRefreshToken } from './store/auth';
import { initConsentAwareTags } from './lib/tagLoader';

// Route-based code splitting — each page is a separate chunk
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const SignInPage = lazy(() => import('./pages/SignInPage').then((m) => ({ default: m.SignInPage })));
const CallbackPage = lazy(() => import('./pages/CallbackPage').then((m) => ({ default: m.CallbackPage })));
const UploadPage = lazy(() => import('./pages/UploadPage').then((m) => ({ default: m.UploadPage })));
const ValidatePage = lazy(() => import('./pages/ValidatePage').then((m) => ({ default: m.ValidatePage })));
const EmailPage = lazy(() => import('./pages/EmailPage').then((m) => ({ default: m.EmailPage })));
const VerifyPage = lazy(() => import('./pages/VerifyPage').then((m) => ({ default: m.VerifyPage })));
const ProcessingPage = lazy(() => import('./pages/ProcessingPage').then((m) => ({ default: m.ProcessingPage })));
const ResultsPage = lazy(() => import('./pages/ResultsPage').then((m) => ({ default: m.ResultsPage })));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage').then((m) => ({ default: m.AnalysisPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const AccountPage = lazy(() => import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })));
const EnterpriseTeamPage = lazy(() => import('./pages/EnterpriseTeamPage').then((m) => ({ default: m.EnterpriseTeamPage })));
const ReportPage = lazy(() => import('./pages/ReportPage').then((m) => ({ default: m.ReportPage })));
const SharePage = lazy(() => import('./pages/SharePage').then((m) => ({ default: m.SharePage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const SecurityInfoPage = lazy(() => import('./pages/SecurityInfoPage').then((m) => ({ default: m.SecurityInfoPage })));
const DeepAnalysisPage = lazy(() => import('./pages/DeepAnalysisPage').then((m) => ({ default: m.DeepAnalysisPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const DemoLandingPage = lazy(() => import('./demo/DemoLandingPage').then((m) => ({ default: m.DemoLandingPage })));
const DemoPage = lazy(() => import('./demo/DemoPage').then((m) => ({ default: m.DemoPage })));
const AssistantButtonDemoPage = lazy(() =>
  import('./demo/AssistantButtonDemoPage').then((m) => ({ default: m.AssistantButtonDemoPage })),
);
const StatusDemoPage = lazy(() =>
  import('./demo/StatusDemoPage').then((m) => ({ default: m.StatusDemoPage })),
);
const ToolbarDemoPage = lazy(() =>
  import('./demo/ToolbarDemoPage').then((m) => ({ default: m.ToolbarDemoPage })),
);
const CreditBannerDemoPage = lazy(() =>
  import('./demo/CreditBannerDemoPage').then((m) => ({ default: m.CreditBannerDemoPage })),
);
const CalloutGalleryDemoPage = lazy(() =>
  import('./demo/CalloutGalleryDemoPage').then((m) => ({ default: m.CalloutGalleryDemoPage })),
);
const WorkflowPickerPage = lazy(() => import('./pages/WorkflowPickerPage').then((m) => ({ default: m.WorkflowPickerPage })));
// RFC-0206 Surface 1 — the product Workspace. Lazy so the dashboard path pays
// nothing for the folder tree, PDF preview or document modals.
const WorkspacePage = lazy(() => import('./pages/WorkspacePage').then((m) => ({ default: m.WorkspacePage })));
const CaseWorkspacePage = lazy(() => import('./pages/CaseWorkspacePage').then((m) => ({ default: m.CaseWorkspacePage })));
const CaseWorkspaceLayout = lazy(() => import('./pages/CaseWorkspaceLayout').then((m) => ({ default: m.CaseWorkspaceLayout })));
const CaseWorkItemsPage = lazy(() => import('./pages/CaseWorkItemsPage').then((m) => ({ default: m.CaseWorkItemsPage })));
const DraftingSetupPage = lazy(() => import('./pages/DraftingSetupPage').then((m) => ({ default: m.DraftingSetupPage })));

/** Redirect while preserving query string (e.g. ?email= from landing page). */
function RedirectWithParams({ to }: { to: string }) {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
}

/** Force full remount when jobId changes (retry after failure). Without this,
 *  React reuses the component instance and stale refs (errorHandled, completionHandled)
 *  from the previous run kill the polling loop before it starts. */
function ProcessingPageKeyed() {
  const { jobId } = useParams();
  return <ProcessingPage key={jobId} />;
}

// Preload critical route chunks after initial render (perf optimization)
function preloadCriticalRoutes() {
  requestIdleCallback(() => {
    void import('./pages/LoginPage');
    void import('./pages/DashboardPage');
    void import('./pages/ReportPage');
    void import('./pages/UploadPage');
  });
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingPlaceholder message="Loading…" />
    </div>
  );
}

/** Inner shell — lives inside BrowserRouter so it can use router hooks */
function AppShell() {
  const captureUtm = useTrackingStore((s) => s.captureUtm);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Bootstrap auth on first mount: if we have a refresh token in
  // localStorage but no in-memory access token (e.g. after a hard reload
  // or Vite HMR reset), restore the session before any component fires
  // its first authenticated request. RequireAuth still does this for
  // gated routes, but mounting it at App level closes the gap for
  // components that live above the route guards (e.g. the assistant
  // chat panel in AppLayout) and avoids a "session expired" flash on
  // the first chat message after a reload.
  useEffect(() => {
    if (isAuthenticated) return;
    if (!getStoredRefreshToken()) return;
    void restoreSessionFromRefreshToken();
  }, [isAuthenticated]);

  // Dev-only console hooks for debugging auth state. Gated to
  // ``import.meta.env.DEV`` so they're stripped from production bundles.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as Record<string, unknown>;
    w.__attainAuth = useAuthStore;
    w.__attainRestoreSession = restoreSessionFromRefreshToken;
  }, []);

  // Capture UTM params from URL on first mount + preload critical routes
  useEffect(() => {
    captureUtm();
    preloadCriticalRoutes();
  }, [captureUtm]);

  // Re-load ad/analytics tags when consent changes in another tab
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === 'cookie_preferences') {
        initConsentAwareTags();
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Move focus to #main-content on route changes (a11y)
  useRouteFocus();

  // Proactively refresh the session ~60s before token expiry so the user
  // never hits a silent 401 (RFC-0202 §"Session-Loss UX" §4).
  useSessionExpiryWatcher();

  return (
    <>
      <SkipToContent />
      <ErrorBanner />
      <UpdateAvailableNotice />

      {/* Live region for screen-reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" id="route-announcer" />

      <Suspense fallback={<PageLoader />}>
        <EnterpriseAccountGuard>
          <Routes>
            {/* Redirect root to login, preserving query params (e.g. ?email= from landing page) */}
            <Route index element={<RedirectWithParams to="/login" />} />

            {/* Auth pages - standalone, no layout */}
            <Route path="login" element={<LoginPage />} />
            <Route path="signin" element={<SignInPage />} />
            <Route path="auth/callback" element={<CallbackPage />} />
            <Route path="verify" element={<VerifyPage />} />

            {/* Upload — requires auth.
                /upload                        → new analysis (legacy entrypoint,
                                                also handles retry via ?caseId=&workItemId=)
                /upload/new                    → workflow picker (Anthony's redesign)
                /upload/:workflowId            → pinned setup form for a chosen workflow
                                                (UploadPage reads the param; reserved
                                                wiring lands in step 2)

                The /upload route preserves develop's retry contract unchanged so
                every existing 'Fix & Re-upload' navigation continues to work. */}
            <Route
              path="upload"
              element={
                <RequireAuth>
                  <UploadPage />
                </RequireAuth>
              }
            />
            <Route
              path="upload/new"
              element={
                <RequireAuth>
                  <WorkflowPickerPage />
                </RequireAuth>
              }
            />
            <Route
              path="upload/:workflowId"
              element={
                <RequireAuth>
                  <UploadPage />
                </RequireAuth>
              }
            />
            {/* Patent Drafting bootstrap wizard (mode selection → materials →
                start). Drafting is chat-orchestrated, so it has its own setup
                flow rather than the shared UploadForm. */}
            <Route
              path="draft/new"
              element={
                <RequireAuth>
                  <DraftingSetupPage />
                </RequireAuth>
              }
            />

            <Route path="validate" element={<ValidatePage />} />
            <Route path="email" element={<EmailPage />} />

            {/* Local preview for the animated Assistant chat button (public, no auth) */}
            <Route path="demo/assistant-button" element={<AssistantButtonDemoPage />} />
            {/* Local preview for the consolidated status primitives (public, no auth) */}
            <Route path="demo/status" element={<StatusDemoPage />} />
            {/* Local preview for the consolidated document-viewer toolbar (public, no auth) */}
            <Route path="demo/toolbar" element={<ToolbarDemoPage />} />
            {/* Local preview for the insufficient-credits banner in the setup form (public, no auth) */}
            <Route path="demo/credit-banner" element={<CreditBannerDemoPage />} />
            {/* Local preview: every Callout intent x tone in one view (public, no auth) */}
            <Route path="demo/callouts" element={<CalloutGalleryDemoPage />} />

            {/* Protected pages */}
            <Route
              path="dashboard"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            {/* RFC-0206 Surface 1 — product Workspace (case browser +
                case-scoped document workspace). Additive; the slot-first
                wizard routes above are untouched.

                ENTERPRISE ONLY (RFC-0206 D6). Self-service has no left nav and
                so nothing links here, but "nothing links here" is not a gate —
                a bookmark or a shared URL would still render it. RequireWorkspace
                is the gate. */}
            <Route
              path="workspace"
              element={
                <RequireAuth>
                  <RequireWorkspace>
                    <WorkspacePage />
                  </RequireWorkspace>
                </RequireAuth>
              }
            />
            {/* The case-scoped routes share ONE SSE subscription, owned by the
                layout (RFC-0206 §"Subscription topology"). The documents view is
                the index child; the W6 work-items view joins as a sibling child
                so navigating between them never tears the connection down. */}
            <Route
              path="workspace/:caseId"
              element={
                <RequireAuth>
                  <RequireWorkspace>
                    <CaseWorkspaceLayout />
                  </RequireWorkspace>
                </RequireAuth>
              }
            >
              <Route index element={<CaseWorkspacePage />} />
              {/* W6 shipped the case-scoped work-item browser here. W12 folded
                  it into the case workspace's right pane, so this path is a
                  DESTINATION no longer — but it stays as a redirect, because W6
                  shipped it and deep links to it exist. Keeping both a page and
                  a pane would be two ways to see one thing, which is how
                  surfaces drift (RFC-0206 A1).
                  With the flag OFF the standalone page is still the only way to
                  reach work items, so the redirect is conditional. When the
                  two-pane comparison settles, the flag, this condition and
                  CaseWorkItemsPage all go. */}
              <Route
                path="workitems"
                element={
                  features.workspaceTwoPane ? (
                    <Navigate to=".." relative="path" replace />
                  ) : (
                    <CaseWorkItemsPage />
                  )
                }
              />
            </Route>
            <Route
              path="account"
              element={
                <RequireAuth>
                  <AccountPage />
                </RequireAuth>
              }
            />
            {/* Enterprise in-product Team/Admin section (RFC-0211/0212 A2) —
                org_admin only, gated by RequireOrgAdmin (accountType===enterprise
                + the org_admin role). Never the platform_admin ops console. */}
            <Route
              path="team"
              element={
                <RequireAuth>
                  <RequireOrgAdmin>
                    <EnterpriseTeamPage />
                  </RequireOrgAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="processing/:caseId/:workItemId/:jobId"
              element={
                <RequireAuth>
                  <ProcessingPageKeyed />
                </RequireAuth>
              }
            />
            <Route
              path="results/:caseId/:workItemId"
              element={
                <RequireAuth>
                  <ResultsPage />
                </RequireAuth>
              }
            />
            {/* RFC §5.2 dev fixture preview — server-side fixture only. 404s
                from SS API when the env's `dev_fixtures` toggle is off. */}
            <Route
              path="results-fixture/:fixtureId"
              element={
                <RequireAuth>
                  <ResultsPage />
                </RequireAuth>
              }
            />
            <Route
              path="reports/:id"
              element={
                <RequireAuth>
                  <ReportPage />
                </RequireAuth>
              }
            />

            {/* Analysis page - standalone */}
            <Route path="analysis" element={<AnalysisPage />} />

            {/* Demo pages - no auth required */}
            <Route path="demo" element={<DemoLandingPage />} />
            <Route path="demo/:scenarioId" element={<DemoPage />} />

            {/* Legal pages - standalone, no auth */}
            <Route path="terms" element={<TermsPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="security" element={<SecurityInfoPage />} />
            <Route path="deep-analysis" element={<DeepAnalysisPage />} />

            {/* Share link - handles redirect */}
            <Route path="/s/:token" element={<SharePage />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </EnterpriseAccountGuard>
      </Suspense>
      <CookieConsent />
    </>
  );
}

export function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/+$/, '') || '/'}>
      <AppShell />
    </BrowserRouter>
  );
}
